import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UsersService } from 'src/app/services/users/users.service';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { MatSelectChange } from '@angular/material/select';
import { CustomValidators } from 'src/app/utils/validators';
import { OrgsService } from 'src/app/services/orgs/orgs.service';
import { OrgModel } from 'src/app/models/org.model';
import { toCreateUserModel, UserForm } from 'src/app/models/user-form.model';
import { UserInfo } from 'src/app/models/user-info.model';

@Component({
  selector: 'app-user-form-dialog',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class UserFormDialogComponent {
  username: string;
  currentUser: UserInfo = new UserInfo();
  isErrorUpdate = false;
  isBusy = false;

  error: Error = { error: { message: '', code: '' } };

  phoneValidator = '^[0-9]*$';
  avsValidator = '[0-9]{3}\\.[0-9]{4}\\.[0-9]{4}\\.[0-9]{2}';
  dateOfBirthValidation =
    '^([0]?[1-9]|[1|2][0-9]|[3][0|1])[.]([0]?[1-9]|[1][0-2])[.]([0-9]{4}|[0-9]{2})$';
  NameNotNumberValidation = '^([^0-9]*)$';

  maxlength = 30;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  preferredLanguage = [
    {
      id: 1,
      name: 'Français',
      acronym: 'fr',
      lang: 'fr',
    },
    {
      id: 2,
      name: 'Deutsch',
      acronym: 'de',
      lang: 'de',
    },
  ];

  cantons: string[] = [
    'VS',
    'ZH',
    'BE',
    'LU',
    'UR',
    'SZ',
    'OW',
    'NW',
    'GL',
    'ZG',
    'FR',
    'SO',
    'BS',
    'BL',
    'SH',
    'AR',
    'AI',
    'SG',
    'GR',
    'AG',
    'TG',
    'TI',
    'VD',
    'NE',
    'GE',
    'JU',
  ];

  defaultDial = '+41';
  activateNotification = true;

  // Pwd
  addUserForm: FormGroup;
  isUserCreated = false;

  // Role
  rolesAvailable: OrgModel[];

  selectedGE = '';

  constructor(
    public matDialogRef: MatDialogRef<UserFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) _data: { action: string; currentUser: UserInfo },
    private _formBuilder: FormBuilder,
    private _userService: UsersService,
    private _translateService: TranslateService,
    private _orgsService: OrgsService
  ) {
    this.isBusy = false;

    this.currentUser = _data.currentUser;

    this.selectedGE = this.currentUser.groupEtablissement[0];

    this.addUserForm = this._formBuilder.group(
      {
        avs: ['', [Validators.required, Validators.pattern(this.avsValidator)]],
        officialFirstName: [
          '',
          Validators.pattern(this.NameNotNumberValidation),
        ],
        officialLastName: [
          '',
          Validators.pattern(this.NameNotNumberValidation),
        ],
        dateOfBirth: [
          '',
          [Validators.required, Validators.pattern(this.dateOfBirthValidation)],
        ],
        gender: [{ value: '', disabled: false }],
        firstName: ['', Validators.pattern(this.NameNotNumberValidation)],
        lastName: ['', Validators.pattern(this.NameNotNumberValidation)],
        defaultDial: [''],
        title: [''],
        classe: [''],
        phone: [
          '',
          [Validators.pattern(this.phoneValidator), Validators.minLength(9)],
        ],
        f2aCondition: ['', Validators.required],
        email: [
          '',
          [
            Validators.required,
            CustomValidators.validEmail(),
            CustomValidators.EmailInterdiction(),
          ],
        ],
        preferredLanguage: ['', Validators.required],
        password: [
          '',
          [
            Validators.required,
            CustomValidators.passwordRestriction(),
            CustomValidators.passwordNameValidator(
              this.currentUser.officialFirstName,
              this.currentUser.officialLastName
            ),
            Validators.minLength(10),
            Validators.maxLength(50),
          ],
        ],
        passwordConfirmation: [
          '',
          [
            Validators.required,
            CustomValidators.passwordRestriction(),
            Validators.minLength(10),
            Validators.maxLength(50),
          ],
        ],

        address: ['', [Validators.minLength(5), Validators.maxLength(50)]],
        zip: [
          '',
          [
            Validators.pattern('^[0-9]*$'),
            Validators.minLength(4),
            Validators.maxLength(10),
          ],
        ], // Must only be number
        locality: [
          '',
          [Validators.pattern('^([^0-9]*)$'), Validators.maxLength(30)],
        ],
        canton: [''],
        grpEtablissement: [''],
        roles: [{ value: [''], disabled: false }],
      },
      {
        validators: [
          CustomValidators.CheckUserFirstName(),
          CustomValidators.CheckUserLastName(),
          CustomValidators.confirmPasswords(),
        ],
      }
    );

    this.addUserForm.get('grpEtablissement').setValue(this.selectedGE);
    this.addUserForm.get('defaultDial').setValue('+41');

    this.initForm();
  }

  onSubmit() {
    const user: UserForm = this.addUserForm.value as UserForm;

    this.isErrorUpdate = false;
    this.isUserCreated = false;
    this._userService.createUser(toCreateUserModel(user)).subscribe({
      next: r => {
        this.username = r.username;
        this.isUserCreated = true;
        this.isBusy = false;
      },
      error: error => {
        let customError: Error = { error: undefined };
        if (error.statusText == 'Unknown Error') {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        } else {
          if (error.error.errors) {
            customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
          } else if (error.error) {
            customError = error.error;
          }
        }

        if (customError.error === undefined) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        }
        this.isErrorUpdate = true;
        this.error = customError;
        this.isUserCreated = false;
        this.isBusy = false;
      },
      complete: () => {
        this.addUserForm.reset();
        this.addUserForm.controls['password'].setErrors(null);
        this.addUserForm.controls['passwordConfirmation'].setErrors(null);
      },
    });
  }

  getErrorMessage(): string {
    return this.addUserForm.get('password').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.addUserForm.get('password').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.addUserForm.get('password').hasError('oldpasswordmatch')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.addUserForm.get('password').hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.addUserForm.get('password').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.addUserForm.get('password').hasError('maxlength')
                ? this._translateService.instant('ERROR.MAX')
                : '';
  }

  async initForm() {
    await this.getRolesForUser(this.selectedGE);
  }

  async getRolesForUser(grpEtablissement: string) {
    try {
      this.isBusy = true;
      await lastValueFrom(
        this._orgsService.getMyAssignableRole(grpEtablissement)
      ).then(response => {
        this.rolesAvailable = response.sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        );
      });
    } catch {
      /* empty */
    } finally {
      this.isBusy = false;
    }
  }

  async onChangeEtablissement($event: MatSelectChange) {
    await this.getRolesForUser($event.value);
  }
}
